import { useEffect } from 'react';

const DEFAULT_HOLD_DELTA = 160;

// Rundungstoleranz in Pixeln: Sub-Pixel-Layout kann `getBoundingClientRect().top`/`scrollY`
// minimal von einem ganzzahligen Zielwert abweichen lassen, ohne dass die Leiste wirklich an
// anderer Stelle sitzt. Dieselbe Toleranz entscheidet, ob die Seite noch „an der Andock-
// Position" gilt UND ob die Leiste schon als geklebt zu behandeln ist.
const PIXEL_EPSILON = 1;

function stickyOffset() {
  const stil = getComputedStyle(document.documentElement);
  const lesen = (name) => parseFloat(stil.getPropertyValue(name)) || 0;
  return lesen('--app-header-offset') + lesen('--profile-banner-offset');
}

// Prüft, ob irgendein Vorfahre von `target` (bis ausschließlich `document.documentElement`)
// ein eigener Scroll-Container ist, der in Richtung `deltaY` noch scrollen kann. Der Magnet
// darf dessen Rad-Ereignis nicht abfangen — sonst verschluckt er die ersten ~160px Scroll-Weg
// innerer Container wie `.catalog-panel__body`, `.command-bridge__scroll` oder Radix-Dialoge,
// die genau an der Andock-Position aufgehängt sein können.
function findScrollableAncestor(target, deltaY) {
  let node = target instanceof Element ? target : target?.parentElement ?? null;
  while (node && node !== document.documentElement) {
    const style = getComputedStyle(node);
    const scrollable = (style.overflowY === 'auto' || style.overflowY === 'scroll')
      && node.scrollHeight > node.clientHeight + 1;
    if (scrollable) {
      const canScrollFurther = deltaY > 0
        ? node.scrollTop + node.clientHeight < node.scrollHeight - 1
        : node.scrollTop > 0;
      if (canScrollFurther) return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Mausrad-Magnet an der Andock-Position des Editors (Oberkante der Sticky-Steuerzeile):
 * Wer die Position mit dem Rad überquert, landet exakt dort; dort bleibt die Seite stehen,
 * bis `holdDelta` Rad-Weg zusammengekommen ist, dann scrollt sie normal weiter. Kein
 * Zurückscrollen, keine Animation. Greift nur in Rad-Ereignisse ein — programmatisches
 * Scrollen (Kategorie-Sprünge, Mana-Kurve) bleibt unberührt; Zoom-Gesten (ctrlKey) ebenso.
 *
 * `.de-controls` selbst ist `position: sticky` — sobald sie klebt, hält der Browser ihr
 * `getBoundingClientRect().top` auf dem Sticky-Versatz fest, egal wie weit weiter gescrollt
 * wird. Eine Neuberechnung der Andock-Position aus diesem Wert würde dann immer die aktuelle
 * `scrollY` zurückgeben (die Leiste „klebt" ja genau dort) statt der echten Zielposition, und
 * die Seite hielte auf der ganzen Matte fälschlich für angedockt. Deshalb wird die Position nur
 * übernommen, solange die Leiste nachweislich noch NICHT klebt (`top` klar über dem
 * Sticky-Versatz); geklebt bleibt der zuletzt so gemessene Wert stehen. Liegt noch nie eine
 * solche Messung vor (z.B. Reload mit wiederhergestellter Scroll-Position tief auf der Matte,
 * erstes Rad-Ereignis dort), greift der Magnet gar nicht ein — er wartet, bis der Nutzer die
 * Leiste einmal unverklemmt sieht, und heilt sich dadurch von selbst.
 */
export function useDockSnap(controlsRef, { holdDelta = DEFAULT_HOLD_DELTA, enabled = true } = {}) {
  useEffect(() => {
    if (!enabled) return undefined;
    let gehalten = 0;
    let dock = null;

    const onWheel = (event) => {
      if (event.ctrlKey || !controlsRef.current) return;
      if (findScrollableAncestor(event.target, event.deltaY)) return;
      const offset = stickyOffset();
      const top = controlsRef.current.getBoundingClientRect().top;
      if (top > offset + PIXEL_EPSILON) {
        dock = Math.round(top + window.scrollY - offset);
      }
      if (dock === null) return;

      const y = Math.round(window.scrollY);
      const ziel = y + event.deltaY;

      if (Math.abs(y - dock) <= PIXEL_EPSILON) {
        gehalten += Math.abs(event.deltaY);
        if (gehalten < holdDelta) event.preventDefault();
        return;
      }
      const überquert = (y < dock && ziel > dock) || (y > dock && ziel < dock);
      if (überquert) {
        event.preventDefault();
        gehalten = 0;
        window.scrollTo({ top: dock, left: window.scrollX });
      }
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, [controlsRef, holdDelta, enabled]);
}
